import { Box, Dialog, DialogContent, Divider, Stack } from "@mui/material";
import { Button, FoodsIcon, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

type PostUsedInActionPlanModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
};

export const PostUsedInActionPlanModal = (props: PostUsedInActionPlanModalProps) => {
    const { isOpen, onClose, onUpdate } = props;
    const { t } = useTranslation();

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                },
            }}
        >
            <DialogContent sx={{ p: 4, position: "relative" }}>
                <Box sx={{ position: "absolute", top: 16, right: 16 }}>
                    <FoodsIconButton icon="close" size="m" buttonProps={{ onClick: onClose }} />
                </Box>

                <Stack gap={2}>
                    <FoodsIcon icon="warning" size={4} />

                    <Typography variant="h3" sx={{ pr: 5 }}>
                        {t(
                            "questionnaire.used_in_action_plan.modal.title",
                            "This questionnaire is used as a reference for your action plan. Are you sure you want to modify it?",
                        )}
                    </Typography>

                    <Box>
                        <Typography variant="body-bold" component="span">
                            {t(
                                "questionnaire.used_in_action_plan.modal.description_bold",
                                "This change will make your action plan outdated.",
                            )}
                        </Typography>{" "}
                        <Typography variant="body-medium" component="span">
                            {t(
                                "questionnaire.used_in_action_plan.modal.description",
                                "When you return to the action plan, you will have to choose between:",
                            )}
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                            <li>
                                <Typography variant="body-medium">
                                    {t(
                                        "questionnaire.used_in_action_plan.modal.option.recalculate",
                                        "Recalculate all actions based on the updated questionnaire",
                                    )}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body-medium">
                                    {t(
                                        "questionnaire.used_in_action_plan.modal.option.recreate",
                                        "Delete and recreate your action plan from scratch",
                                    )}
                                </Typography>
                            </li>
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>

            <Divider />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    px: 4,
                    pt: 4,
                    pb: 4.5,
                }}
            >
                <Button onClick={onUpdate} variant="primary" fullWidth>
                    {t("questionnaire.used_in_action_plan.modal.continue_btn", "Yes, continue")}
                </Button>
                <Button onClick={onClose} variant="ternary" fullWidth>
                    {t("questionnaire.used_in_action_plan.modal.cancel_btn", "No, cancel")}
                </Button>
            </Box>
        </Dialog>
    );
};
